import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Content, Stack, Heading } from '@kinesis/bungle';
import { Loading } from '@/components/loading';
import { Layout } from '@/components/layout';
import { ServiceAccountConnectors } from '@/components/service-account-connectors';
import { useNow } from '@/hooks/use-now';
import {
  useListServiceAccountOauthClientsQuery,
  useNewServiceAccountOauthClientMutation,
  useDeleteServiceAccountOauthClientMutation,
} from '@/api/organisation';
import type { ServiceAccountOauthClientSecret } from '@/api/organisation';

const SettingsOrganisationServiceAccountConnectors = () => {
  const { accountId } = useParams<{
    organisationId: string;
    accountId: string;
  }>();
  const accId = Number(accountId);
  const now = useNow();

  const { data: connectors, isLoading } =
    useListServiceAccountOauthClientsQuery({
      accountId: accId,
    });
  const [createConnector, { isLoading: isCreating }] =
    useNewServiceAccountOauthClientMutation();
  const [deleteConnector] = useDeleteServiceAccountOauthClientMutation();

  const [newConnector, setNewConnector] = useState<
    ServiceAccountOauthClientSecret | undefined
  >();

  const onCreate = useCallback(
    async (name: string, redirectUris: string[]) => {
      const credential = await createConnector({
        accountId: accId,
        name,
        redirectUris,
      }).unwrap();
      setNewConnector(credential);
    },
    [accId, createConnector],
  );

  const onDelete = useCallback(
    (clientId: string) => {
      deleteConnector({ accountId: accId, clientId });
    },
    [accId, deleteConnector],
  );

  if (isLoading || !connectors) {
    return <Loading />;
  }

  return (
    <Layout variant='narrow'>
      <Content>
        <Stack space='medium'>
          <Heading>Connectors</Heading>
          <ServiceAccountConnectors
            connectors={connectors}
            now={now}
            isCreating={isCreating}
            newConnector={newConnector}
            onCreate={onCreate}
            onDelete={onDelete}
            onDismissNewConnector={() => setNewConnector(undefined)}
          />
        </Stack>
      </Content>
    </Layout>
  );
};

export { SettingsOrganisationServiceAccountConnectors };
